import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api.js';
import RichEditor from '../components/RichEditor.jsx';

function fmtDate(s) { return s ? new Date(s.replace(' ', 'T') + 'Z').toLocaleString('ko-KR') : '-'; }

export default function CowriteEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [title, setTitle] = useState('');
  const [html, setHtml] = useState('');
  const [summary, setSummary] = useState('');
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadErr, setLoadErr] = useState('');

  const load = useCallback(async () => {
    try {
      setLoadErr('');
      const d = await api.getDoc(id);
      setDoc(d);
      setTitle(d.title || '');
      setHtml(d.latest?.content_html || '');
      const vs = await api.docVersions(id);
      setVersions(vs || []);
    } catch (e) {
      setLoadErr(e.message || '문서를 불러오지 못했습니다.');
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (loadErr) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="bg-white border border-red-200 rounded-2xl p-6 shadow-card">
          <h2 className="text-lg font-bold text-red-700 mb-2">문서를 불러오지 못했습니다</h2>
          <p className="text-sm text-red-700">{loadErr}</p>
          <div className="mt-4 flex gap-2">
            <button onClick={load} className="px-3 py-1.5 rounded-md bg-medical-600 hover:bg-medical-700 text-white text-sm font-semibold">다시 시도</button>
            <Link to="/cowrite" className="px-3 py-1.5 rounded-md border border-medical-200 text-medical-700 text-sm">목록으로</Link>
          </div>
        </div>
      </div>
    );
  }

  const save = async () => {
    try {
      await api.saveDoc(id, { title, content_html: html, summary });
      setSummary('');
      load();
    } catch (e) { alert(e.message); }
  };

  const rollback = async (v) => {
    if (!window.confirm(`v${v.version} 으로 롤백하시겠습니까? (새 버전으로 기록됩니다)`)) return;
    try { await api.rollback(id, v.version); load(); setPreview(null); } catch (e) { alert(e.message); }
  };

  const previewVersion = async (v) => {
    const detail = await api.docVersion(id, v.version);
    setPreview(detail);
  };

  if (!doc) return <div className="p-6 text-medical-500">로딩...</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-12 gap-5">
      <div className="col-span-12 md:col-span-9">
        <div className="flex items-center flex-wrap gap-2 mb-3">
          <Link to="/cowrite" className="text-sm text-medical-700 hover:underline">← 문서 목록</Link>
          <span className="ml-auto" />
          <a
            href={api.cowritePdfUrl(id)}
            className="text-sm bg-medical-50 text-medical-700 px-3 py-1.5 rounded-md hover:bg-medical-100 border border-medical-100"
            title="현재 최신 버전을 PDF로 저장합니다"
          >
            PDF 다운로드
          </a>
          <button onClick={() => setShowVersions(!showVersions)} className="text-sm bg-medical-50 text-medical-700 px-3 py-1.5 rounded-md hover:bg-medical-100">
            버전 기록 {showVersions ? '닫기' : `(${versions.length})`}
          </button>
          <button onClick={save} className="text-sm bg-medical-600 hover:bg-medical-700 text-white px-3 py-1.5 rounded-md font-semibold">저장 (새 버전)</button>
        </div>
        <input value={title} onChange={(e) => setTitle(e.target.value)}
          className="w-full mb-3 px-3 py-2 border border-medical-200 rounded-md text-xl font-bold text-medical-800" />
        <div className="text-xs text-medical-500 mb-2">
          작성자: <b>{doc.author_name}</b> ({doc.author_institution}) · 현재 표시: v{doc.latest?.version} ·
          최근 편집: {doc.latest?.editor_name} ({doc.latest?.editor_institution}) · {fmtDate(doc.latest?.edited_at)}
        </div>
        <RichEditor value={html} onChange={setHtml} />
        <div className="mt-2 flex items-center gap-2">
          <input value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="변경 요약 (선택)"
            className="flex-1 px-3 py-1.5 border border-medical-200 rounded text-sm" />
        </div>

        {preview && (
          <div className="mt-5 bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-center mb-2">
              <div className="text-sm font-semibold text-amber-800">v{preview.version} 미리보기</div>
              <span className="ml-auto" />
              <button onClick={() => setPreview(null)} className="text-xs text-amber-700 hover:underline mr-3">닫기</button>
              <button onClick={() => rollback(preview)} className="text-xs bg-amber-600 hover:bg-amber-700 text-white px-2.5 py-1 rounded">이 버전으로 롤백</button>
            </div>
            <div className="text-xs text-amber-700 mb-2">
              제목: {preview.title} · 편집자: {preview.editor_name} ({preview.editor_institution}) · {fmtDate(preview.edited_at)}
              {preview.summary ? ' · ' + preview.summary : ''}
            </div>
            <div className="bg-white rounded p-3 prose-readonly" dangerouslySetInnerHTML={{ __html: preview.content_html }} />
          </div>
        )}
      </div>

      {showVersions && (
        <aside className="col-span-12 md:col-span-3 bg-white rounded-xl shadow-card border border-medical-100 p-3 h-fit">
          <div className="text-sm font-semibold text-medical-800 mb-2">버전 기록</div>
          <div className="space-y-1 max-h-[70vh] overflow-y-auto">
            {versions.map(v => (
              <div key={v.id} className="rounded border border-medical-100 p-2 text-xs hover:bg-medical-50">
                <div className="flex items-center gap-2 break-keep">
                  <div className="font-semibold text-medical-800 shrink-0">v{v.version}</div>
                  <span className="ml-auto text-medical-500 text-[10px] leading-tight text-right">{fmtDate(v.edited_at)}</span>
                </div>
                <div className="text-medical-700 mt-0.5 break-keep">{v.editor_name} <span className="text-medical-400">{v.editor_institution}</span></div>
                {v.summary && <div className="text-medical-500 mt-0.5 break-all">📝 {v.summary}</div>}
                <div className="mt-1 flex gap-2">
                  <button onClick={() => previewVersion(v)} className="text-medical-600 hover:underline">미리보기</button>
                  <button onClick={() => rollback(v)} className="text-amber-700 hover:underline">롤백</button>
                  <a href={api.cowritePdfUrl(id, v.version)} className="text-medical-700 hover:underline ml-auto">PDF</a>
                </div>
              </div>
            ))}
          </div>
        </aside>
      )}
    </div>
  );
}
